import {BaseButton} from "@/components/BaseButton";
import {BaseInput} from "@/components/BaseInput";
import {useCreateUser} from "@/hooks/useUsers";
import {useState} from "react";
import {z} from "zod";

const createUserSchema = z.object({
  email: z.string().email("Email inválido"),
  firstName: z.string().min(1, "Nombre requerido"),
  lastName: z.string().min(1, "Apellido requerido"),
  password: z.string().min(8, "Mínimo 8 caracteres"),
});

type CreateUserForm = z.infer<typeof createUserSchema>;
type FormErrors = Partial<Record<keyof CreateUserForm, string>>;

interface CreateUserModalProps {
  onClose: () => void;
}

export function CreateUserModal({onClose}: CreateUserModalProps) {
  const createUser = useCreateUser();
  const [form, setForm] = useState<CreateUserForm>({email: "", firstName: "", lastName: "", password: ""});
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState("");

  const setField = (field: keyof CreateUserForm, value: string) => {
    setForm(prev => ({...prev, [field]: value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
    if (serverError) setServerError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = createUserSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: FormErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof CreateUserForm;
        fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    const response = await createUser.mutateAsync(result.data);
    if (!response.ok) {
      setServerError(response.error);
      return;
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        onKeyDown={() => {}}
        role="presentation"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4">Crear usuario</h2>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
          noValidate
        >
          <BaseInput
            label="Email"
            type="email"
            placeholder="usuario@email.com"
            value={form.email}
            onChange={e => setField("email", e.target.value)}
            error={errors.email}
          />
          <div className="grid grid-cols-2 gap-3">
            <BaseInput
              label="Nombre"
              placeholder="Juan"
              value={form.firstName}
              onChange={e => setField("firstName", e.target.value)}
              error={errors.firstName}
            />
            <BaseInput
              label="Apellido"
              placeholder="Pérez"
              value={form.lastName}
              onChange={e => setField("lastName", e.target.value)}
              error={errors.lastName}
            />
          </div>
          <BaseInput
            label="Contraseña"
            type="password"
            placeholder="••••••••"
            value={form.password}
            onChange={e => setField("password", e.target.value)}
            error={errors.password}
          />

          {serverError && (
            <div
              className="px-4 py-3 text-sm rounded-md"
              style={{
                backgroundColor: "var(--color-danger-bg)",
                color: "var(--color-danger-text)",
                border: "1px solid var(--color-danger)",
              }}
            >
              {serverError}
            </div>
          )}

          <div className="flex items-center gap-3 justify-end mt-2">
            <BaseButton
              variant="secondary"
              size="md"
              type="button"
              onClick={onClose}
            >
              Cancelar
            </BaseButton>
            <BaseButton
              variant="primary"
              size="md"
              type="submit"
              loading={createUser.isPending}
            >
              Crear
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  );
}
